import React, { useState, useEffect } from 'react';

import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import {
  User,
  ShoppingBag,
  Bell,
  Star,
  TrendingUp,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Crown,
  Award,
  Calendar,
  DollarSign
} from 'lucide-react';
import axios from 'axios';
import { API_URLS } from '../config/api';

const UserDashboard = () => {
  const [user, setUser] = useState({
    full_name: 'Demo Kullanıcı',
    email: 'demo@website-al.com',
    level: 'Bronze',
    points: 150,
    profile_picture: null
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const baseURL = API_URLS.ADMIN.ADMIN_API.replace('/admin_api.php', '');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [dashboardResponse, notificationResponse] = await Promise.all([
          axios.get(API_URLS.AUTH.DASHBOARD_STATS),
          axios.get(API_URLS.AUTH.NOTIFICATIONS)
        ]);

        setDashboardData(dashboardResponse.data);
        setNotifications(notificationResponse.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [baseURL]);

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.patch(`${baseURL}/api/auth/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'Bronze': 'from-orange-400 to-orange-600',
      'Silver': 'from-gray-400 to-gray-600',
      'Gold': 'from-yellow-400 to-yellow-600',
      'Platinum': 'from-blue-400 to-blue-600',
      'Diamond': 'from-purple-400 to-purple-600'
    };
    return colors[level] || 'from-gray-400 to-gray-600';
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'Diamond': return Crown;
      case 'Platinum': return Award;
      default: return Star;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const recentOrders = dashboardData?.recent_orders || [];
  const recentNotifications = dashboardData?.recent_notifications || [];

  const LevelIcon = getLevelIcon(user.level);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Hoş geldiniz, {user.full_name}!
              </h1>
              <p className="text-gray-600 mt-2">Dashboard'unuzda tüm aktivitelerinizi takip edebilirsiniz</p>
            </div>

            {/* User Level Badge */}
            <div className={`bg-gradient-to-r ${getLevelColor(user.level)} text-white px-6 py-3 rounded-xl flex items-center space-x-2`}>
              <LevelIcon className="w-6 h-6" />
              <div>
                <div className="font-bold">{user.level}</div>
                <div className="text-sm opacity-90">{user.points} puan</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <ShoppingBag className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Toplam Sipariş</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bekleyen</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pending_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tamamlanan</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.completed_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Bell className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bildirimler</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.unread_notifications || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Level Progress */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5" />
              <span>Seviye İlerlemesi</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Mevcut Seviye: {user.level}</span>
                <span className="text-sm text-gray-600">
                  {user.points} / {stats.next_level_points} puan
                </span>
              </div>
              <Progress
                value={(user.points / stats.next_level_points) * 100}
                className="h-3"
              />
              <p className="text-sm text-gray-600">
                Bir sonraki seviyeye {stats.next_level_points - user.points} puan kaldı!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-3">
            <TabsTrigger value="orders" className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span>Siparişlerim</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center space-x-2">
              <Bell className="w-4 h-4" />
              <span>Bildirimler</span>
              {stats.unread_notifications > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">
                  {stats.unread_notifications}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>Profil</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>Son Siparişlerim</CardTitle>
              </CardHeader>
              <CardContent>
                {recentOrders.length > 0 ? (
                  <div className="space-y-4">
                    {recentOrders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-medium">{order.service_name}</h4>
                          <p className="text-sm text-gray-600">{order.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(order.created_at).toLocaleDateString('tr-TR')}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge
                            variant={order.status === 'completed' ? 'default' : 'secondary'}
                            className="mb-2"
                          >
                            {order.status === 'pending' ? 'Bekliyor' :
                              order.status === 'completed' ? 'Tamamlandı' : order.status}
                          </Badge>
                          <p className="font-bold text-lg">₺{order.amount.toLocaleString('tr-TR')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz sipariş yok</h3>
                    <p className="text-gray-600">İlk siparişinizi verin ve 100 puan kazanın!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Bildirimler</CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length > 0 ? (
                  <div className="space-y-3">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${notification.is_read
                          ? 'bg-gray-50 border-gray-200'
                          : 'bg-blue-50 border-blue-200'
                          }`}
                        onClick={() => !notification.is_read && markNotificationRead(notification.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium flex items-center space-x-2">
                              <span>{notification.title}</span>
                              {!notification.is_read && (
                                <Badge variant="secondary" className="text-xs">Yeni</Badge>
                              )}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {new Date(notification.created_at).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                          <div className="ml-4">
                            <Badge
                              variant={notification.type === 'success' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {notification.type}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Bildirim yok</h3>
                    <p className="text-gray-600">Yeni bildirimler burada görünecek</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profil Bilgileri</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Ad Soyad</Label>
                    <p className="text-lg font-medium">{user.full_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">E-posta</Label>
                    <p className="text-lg font-medium">{user.email}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Şirket</Label>
                    <p className="text-lg font-medium">{user.company_name || 'Belirtilmemiş'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Telefon</Label>
                    <p className="text-lg font-medium">{user.phone || 'Belirtilmemiş'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Hesap Türü</Label>
                    <p className="text-lg font-medium capitalize">{user.user_type}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Üyelik Tarihi</Label>
                    <p className="text-lg font-medium">
                      {new Date(user.created_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>

                <div className="pt-6 border-t">
                  <Button className="bg-purple-600 hover:bg-purple-700">
                    Profili Düzenle
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserDashboard;